import { type Address, erc20Abi, type PublicClient } from 'viem'
import type { NormalizedTokenInfo } from './types'

/**
 * Fetch token info directly from onchain (fallback when indexer is unavailable)
 */
export async function fetchTokenOnchain(
  publicClient: PublicClient,
  chainId: number,
  address: Address,
): Promise<NormalizedTokenInfo> {
  const contracts = [
    {
      address,
      abi: erc20Abi,
      functionName: 'name',
    },
    {
      address,
      abi: erc20Abi,
      functionName: 'symbol',
    },
    {
      address,
      abi: erc20Abi,
      functionName: 'decimals',
    },
  ] as const

  const results = await publicClient.multicall({ contracts })

  const name = results[0].status === 'success' ? (results[0].result as string) : 'Unknown'
  const symbol = results[1].status === 'success' ? (results[1].result as string) : 'UNKNOWN'
  const decimals = results[2].status === 'success' ? (results[2].result as number) : 18

  return {
    chainId,
    address,
    name,
    symbol,
    decimals,
    source: 'onchain',
  }
}

/**
 * Batch fetch multiple tokens from onchain
 */
export async function fetchTokensOnchain(
  publicClient: PublicClient,
  chainId: number,
  addresses: Address[],
): Promise<Map<Address, NormalizedTokenInfo>> {
  const tokenMap = new Map<Address, NormalizedTokenInfo>()

  if (addresses.length === 0) {
    return tokenMap
  }

  // Build contracts array for all tokens
  const contracts = addresses.flatMap((address) => [
    {
      address,
      abi: erc20Abi,
      functionName: 'name' as const,
    },
    {
      address,
      abi: erc20Abi,
      functionName: 'symbol' as const,
    },
    {
      address,
      abi: erc20Abi,
      functionName: 'decimals' as const,
    },
  ])

  const results = await publicClient.multicall({ contracts })

  // Process results in chunks of 3 (name, symbol, decimals per token)
  for (let i = 0; i < addresses.length; i++) {
    const baseIndex = i * 3
    const address = addresses[i]

    if (!address) continue

    const nameResult = results[baseIndex]
    const symbolResult = results[baseIndex + 1]
    const decimalsResult = results[baseIndex + 2]

    const name = nameResult?.status === 'success' ? (nameResult.result as string) : 'Unknown'
    const symbol = symbolResult?.status === 'success' ? (symbolResult.result as string) : 'UNKNOWN'
    const decimals = decimalsResult?.status === 'success' ? (decimalsResult.result as number) : 18

    tokenMap.set(address, {
      chainId,
      address,
      name,
      symbol,
      decimals,
      source: 'onchain',
    })
  }

  return tokenMap
}

